import { DocumentArrowDownIcon } from "@heroicons/react/24/outline"
import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { useAdForgeListCreativesQuery } from "~/ui-rtk/api/adForgeApi"
import { useAgendaControllerListQuery } from "~/ui-rtk/api/agendaApi"
import { useListAuditsQuery } from "~/ui-rtk/api/auditsApi"
import type { AdBlastJobResult } from "~/ui-rtk/api/jobsApi"
import { useKeyEventControllerGetAllKeyEventsQuery } from "~/ui-rtk/api/keyEventsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button, Input } from "~/ui-rtk/components/ui/controls"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import {
  budgetChangeLabel,
  type BudgetUpdateResult,
} from "~/ui-rtk/pages/Jobs/jobViews"
import { auditTypeMeta } from "../constants"
import { useAssignableUsers } from "../hooks/useAssignableUsers"
import { type AuditItem, describeItem, itemName } from "../utils"
import type {
  ReportActionGroup,
  ReportActionItem,
  ReportBlastAd,
  ReportBudgetChange,
  ReportCreativeCard,
  ReportKeyEvent,
} from "../report/agenda-report"

/**
 * Prefetch an image into a JPEG/PNG data URL for react-pdf (which can't
 * render webp and is subject to CORS on remote fetches). Failures return
 * undefined — the card renders without a preview instead of breaking the PDF.
 */
async function toPdfImage(url: string): Promise<string | undefined> {
  try {
    const response = await fetch(url)
    if (!response.ok) return undefined
    const blob = await response.blob()
    if (!["image/jpeg", "image/png"].includes(blob.type)) return undefined
    return await new Promise<string | undefined>(resolve => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(undefined)
      reader.readAsDataURL(blob)
    })
  } catch {
    return undefined
  }
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-xl border border-system-blue-600/40 bg-system-blue-800/60 p-3">
      <div className="text-xl font-semibold text-white">{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wide text-system-gray-500">
        {label}
      </div>
    </div>
  )
}

/**
 * "Company action report" — builds a client-facing PDF from the agenda slots
 * in a chosen period: ads released, creatives produced/approved, budget
 * reviews, per-activity breakdown. The PDF renderer is imported on demand so
 * @react-pdf/renderer stays out of the main bundle.
 */
export default function ReportDialog({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const dayjs = useDate()
  const currentCompany = useCurrentCompany()
  const { users } = useAssignableUsers()

  const [fromDate, setFromDate] = useState(() =>
    dayjs().startOf("month").format("YYYY-MM-DD"),
  )
  const [toDate, setToDate] = useState(() =>
    dayjs().endOf("month").format("YYYY-MM-DD"),
  )
  const [isGenerating, setIsGenerating] = useState(false)

  const range = useMemo(
    () => ({
      from: dayjs(fromDate).startOf("day").toISOString(),
      to: dayjs(toDate).endOf("day").toISOString(),
    }),
    [fromDate, toDate, dayjs],
  )

  const { data: slots = [], isFetching } = useAgendaControllerListQuery(
    { from: range.from, to: range.to },
    { skip: !isOpen },
  )

  // Enrichment sources: the audit history (the durable record of every
  // action — blast per-ad rows, budget changes, the chronological action log)
  // and the creatives library (images + copy for cards). Framework-written
  // audits carry the executing jobRunId, which is what agenda-slot links hold.
  const { data: audits = [], isFetching: isFetchingAudits } =
    useListAuditsQuery(
      { from: range.from, to: range.to, take: 200 },
      { skip: !isOpen },
    )
  const { data: creatives = [] } = useAdForgeListCreativesQuery(undefined, {
    skip: !isOpen,
  })
  const { data: allKeyEvents = [] } = useKeyEventControllerGetAllKeyEventsQuery(
    undefined,
    { skip: !isOpen },
  )

  // Ad set id → human name, via Cube (job results only carry ids). The date
  // range starts 30 days before the report window so ad sets that were quiet
  // during the exact period still resolve.
  const adSetNamesQuery = useMemo(() => {
    if (!currentCompany?.id || !isOpen) return null
    return {
      // Dimensions-only lookup — we just need id → name mappings.
      dimensions: [
        "company_media_metrics_adsets.adset_id",
        "company_media_metrics_adsets.adset_name",
        "company_media_metrics_adsets.campaign_id",
        "company_media_metrics_adsets.campaign_name",
      ],
      filters: [
        {
          member: "company_media_metrics_adsets.companyid",
          operator: "equals" as const,
          values: [currentCompany.id],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
          dateRange: [
            dayjs(fromDate).subtract(30, "day").format("YYYY-MM-DD"),
            dayjs(toDate).format("YYYY-MM-DD"),
          ] as [string, string],
        },
      ],
      limit: 1000,
    }
  }, [currentCompany?.id, isOpen, fromDate, toDate, dayjs])

  const { resultSet: adSetsResultSet, isLoading: isLoadingAdSetNames } =
    useCubeQuery(adSetNamesQuery || {}, {
      skip: !adSetNamesQuery,
    })

  const { adSetNames, campaignNames } = useMemo(() => {
    const adSets: Record<string, string> = {}
    const campaigns: Record<string, string> = {}
    if (!adSetsResultSet) {
      return { adSetNames: adSets, campaignNames: campaigns }
    }
    for (const row of adSetsResultSet.tablePivot()) {
      const id = String(row["company_media_metrics_adsets.adset_id"] ?? "")
      const name = row["company_media_metrics_adsets.adset_name"]
      const campaignId = String(
        row["company_media_metrics_adsets.campaign_id"] ?? "",
      )
      const campaign = row["company_media_metrics_adsets.campaign_name"]
      if (id && name) {
        adSets[id] = campaign ? `${name} (${campaign})` : String(name)
      }
      if (campaignId && campaign) {
        campaigns[campaignId] = String(campaign)
      }
    }
    return { adSetNames: adSets, campaignNames: campaigns }
  }, [adSetsResultSet])

  // Per-ad performance for the ads the linked blast jobs created — we hold
  // their Meta ad ids in the job results, so pull spend / impressions /
  // purchases over the report period from Cube.
  const blastAdIds = useMemo(() => {
    const linkedJobIds = new Set(
      slots.flatMap(s => (s.links ?? []).map(l => l.jobRunId)).filter(Boolean),
    )
    const ids = audits
      .filter(
        audit =>
          audit.type === "ad-blast" &&
          audit.jobRunId &&
          linkedJobIds.has(audit.jobRunId),
      )
      .flatMap(audit => audit.items as AdBlastJobResult["results"])
      .map(row => row.result?.newAdId ?? row.externalAdId)
      .filter((id): id is string => Boolean(id))
    return [...new Set(ids)]
  }, [slots, audits])

  const adMetricsQuery = useMemo(() => {
    if (!currentCompany?.id || !isOpen || blastAdIds.length === 0) return null
    return {
      // ad_name / adset_name double as the display-name fallback — some blast
      // paths don't record newAdName in the job result.
      dimensions: [
        "company_media_metrics_ads.ad_id",
        "company_media_metrics_ads.ad_name",
        "company_media_metrics_ads.adset_id",
        "company_media_metrics_ads.adset_name",
      ],
      measures: [
        "company_media_metrics_ads.spend",
        "company_media_metrics_ads.impressions",
        "company_media_metrics_ads.purchases",
      ],
      filters: [
        {
          member: "company_media_metrics_ads.companyid",
          operator: "equals" as const,
          values: [currentCompany.id],
        },
        {
          member: "company_media_metrics_ads.ad_id",
          operator: "equals" as const,
          values: blastAdIds,
        },
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_ads.md_date",
          dateRange: [
            dayjs(fromDate).format("YYYY-MM-DD"),
            dayjs(toDate).format("YYYY-MM-DD"),
          ] as [string, string],
        },
      ],
      limit: 1000,
    }
  }, [currentCompany?.id, isOpen, blastAdIds, fromDate, toDate, dayjs])

  const { resultSet: adMetricsResultSet, isLoading: isLoadingAdMetrics } =
    useCubeQuery(adMetricsQuery || {}, {
      skip: !adMetricsQuery,
    })

  const adMetrics = useMemo(() => {
    const map: Record<
      string,
      {
        spend: number
        impressions: number
        purchases: number
        adName?: string
        adSetName?: string
      }
    > = {}
    if (!adMetricsResultSet) return map
    for (const row of adMetricsResultSet.tablePivot()) {
      const id = String(row["company_media_metrics_ads.ad_id"] ?? "")
      if (!id) continue
      map[id] = {
        spend: Number(row["company_media_metrics_ads.spend"] ?? 0),
        impressions: Number(row["company_media_metrics_ads.impressions"] ?? 0),
        purchases: Number(row["company_media_metrics_ads.purchases"] ?? 0),
        adName:
          (row["company_media_metrics_ads.ad_name"] as string) || undefined,
        adSetName:
          (row["company_media_metrics_ads.adset_name"] as string) || undefined,
      }
    }
    return map
  }, [adMetricsResultSet])

  // Everything the PDF needs must be resolved before Download unlocks —
  // generating early would silently ship ids instead of names and "—" metrics.
  const isLoadingEnrichment =
    isFetching || isFetchingAudits || isLoadingAdSetNames || isLoadingAdMetrics

  // Lightweight on-screen preview of what the PDF will report.
  const preview = useMemo(() => {
    const units = (type: string, statuses: string[]) =>
      slots
        .filter(s => s.type === type)
        .flatMap(s => s.links ?? [])
        .filter(l => statuses.includes(l.status))
        .reduce((sum, l) => sum + l.units, 0)
    return {
      adsReleased: units("AD_BLAST", ["DELIVERED", "APPROVED"]),
      creativesProduced: units("AD_CREATIVE", ["DELIVERED", "APPROVED"]),
      creativesApproved: units("AD_CREATIVE", ["APPROVED"]),
      budgetReviews: slots.filter(
        s => s.type === "BUDGET_RECOMMENDATION" && s.status === "DONE",
      ).length,
      done: slots.filter(s => s.status === "DONE").length,
      total: slots.length,
    }
  }, [slots])

  const handleGenerate = async () => {
    if (isGenerating) return
    if (slots.length === 0 && audits.length === 0) {
      toast.error("No agenda activity in the selected period")
      return
    }
    setIsGenerating(true)
    try {
      const { generateAgendaReportPdf, agendaReportFileName } = await import(
        "../report/agenda-report"
      )

      // Audits by their executing job run — the join point to slot links.
      const auditsByJobRun = new Map(
        audits
          .filter(audit => audit.jobRunId)
          .map(audit => [audit.jobRunId as string, audit]),
      )

      // Blast slots → per-ad rows from their linked blasts' audit records.
      const blastAdsBySlot: Record<string, ReportBlastAd[]> = {}
      for (const slot of slots) {
        if (slot.type !== "AD_BLAST") continue
        const ads: ReportBlastAd[] = (slot.links ?? [])
          .map(link =>
            link.jobRunId ? auditsByJobRun.get(link.jobRunId) : null,
          )
          .filter(audit => audit?.type === "ad-blast")
          .flatMap(audit => (audit?.items ?? []) as AdBlastJobResult["results"])
          // Client-facing report: only the ads that actually went live.
          .filter(row => row.success)
          .map(row => {
            const adId = row.result?.newAdId ?? row.externalAdId
            const metrics = adId ? adMetrics[adId] : undefined
            return {
              adName:
                row.result?.newAdName ??
                metrics?.adName ??
                row.result?.sourceAdName,
              adSetId: row.adSetId,
              adSetName: adSetNames[row.adSetId] ?? metrics?.adSetName,
              strategy: row.result?.strategy,
              success: row.success,
              spend: metrics?.spend,
              impressions: metrics?.impressions,
              purchases: metrics?.purchases,
            }
          })
        if (ads.length) blastAdsBySlot[slot.id] = ads
      }

      // Creative slots → cards with prefetched images.
      const creativesById = new Map(creatives.map(c => [c.id, c]))
      const creativeCardsBySlot: Record<string, ReportCreativeCard[]> = {}
      for (const slot of slots) {
        if (slot.type !== "AD_CREATIVE") continue
        const linked = (slot.links ?? [])
          .map(link =>
            link.adCreativeId ? creativesById.get(link.adCreativeId) : null,
          )
          .filter(Boolean)
        const cards = await Promise.all(
          linked.map(async (creative): Promise<ReportCreativeCard> => {
            const media = creative?.mediaAssets?.[0]
            const imageUrl = media?.thumbnailUrl || media?.url
            return {
              name: creative?.name ?? "Creative",
              imageDataUrl: imageUrl ? await toPdfImage(imageUrl) : undefined,
              headline: creative?.headline ?? undefined,
              primaryText: creative?.primaryText ?? undefined,
              statusLabel:
                creative?.approvalStatus === "APPROVED"
                  ? "Approved"
                  : (creative?.approvalStatus ?? "")
                      .toLowerCase()
                      .replace(/_/g, " ") || undefined,
              approved: creative?.approvalStatus === "APPROVED",
            }
          }),
        )
        if (cards.length) creativeCardsBySlot[slot.id] = cards
      }

      // Budget slots → what actually changed, from their linked audit records.
      const budgetChangesBySlot: Record<string, ReportBudgetChange[]> = {}
      for (const slot of slots) {
        if (slot.type !== "BUDGET_RECOMMENDATION") continue
        const changes: ReportBudgetChange[] = (slot.links ?? [])
          .map(link =>
            link.jobRunId ? auditsByJobRun.get(link.jobRunId) : null,
          )
          .filter(audit => audit?.type === "budget-update")
          .map(audit => {
            const result = (audit?.items?.[0] ??
              null) as BudgetUpdateResult | null
            const entityId = result?.entityId ?? ""
            const target =
              (result?.entity === "adset"
                ? adSetNames[entityId]
                : campaignNames[entityId]) ??
              `${result?.entity ?? "entity"} ${entityId}`
            return { target, change: budgetChangeLabel(result) }
          })
        if (changes.length) budgetChangesBySlot[slot.id] = changes
      }

      // Key events (launches/promos) overlapping the period — client context
      // for why the work below happened.
      const keyEvents: ReportKeyEvent[] = allKeyEvents
        .filter(keyEvent => {
          const start = dayjs(keyEvent.start)
          const end = dayjs(keyEvent.end ?? keyEvent.start)
          return (
            start.isBefore(dayjs(range.to)) && !end.isBefore(dayjs(range.from))
          )
        })
        .map(keyEvent => ({
          name: keyEvent.name,
          type: keyEvent.type,
          dateLabel:
            keyEvent.end && !dayjs(keyEvent.end).isSame(keyEvent.start, "day")
              ? `${dayjs(keyEvent.start).format("MMM D")} – ${dayjs(keyEvent.end).format("MMM D")}`
              : dayjs(keyEvent.start).format("MMM D"),
          spend: keyEvent.spend ?? undefined,
        }))

      // Recorded actions → grouped by type, one row per successfully
      // completed item. Failed work is left out of the client-facing report.
      // Entity ids resolve to names via the Cube lookups where possible.
      const resolveEntityName = (item: AuditItem): string | undefined => {
        const id = typeof item.entityId === "string" ? item.entityId : undefined
        if (!id) return undefined
        return item.entity === "campaign" ? campaignNames[id] : adSetNames[id]
      }
      const grouped: Record<string, ReportActionItem[]> = {}
      const chronological = [...audits].sort(
        (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      )
      for (const audit of chronological) {
        if (audit.successful === 0) continue
        const items = ((audit.items as AuditItem[]) ?? []).filter(
          item => item?.success !== false,
        )
        const dateLabel = dayjs(audit.createdAt).format("MMM D, HH:mm")
        const typeLabel = auditTypeMeta(audit.type).label
        let rows: ReportActionItem[]
        if (audit.type === "ad-blast") {
          rows = (items as unknown as AdBlastJobResult["results"]).map(row => ({
            dateLabel,
            name: row.result?.newAdName ?? row.result?.sourceAdName ?? "New ad",
            detail: [
              adSetNames[row.adSetId] ?? row.adSetId,
              row.result?.strategy,
            ]
              .filter(Boolean)
              .join(" · "),
          }))
        } else if (items.length > 0) {
          rows = items.map((item, index) => ({
            dateLabel,
            name: resolveEntityName(item) ?? itemName(item, index),
            detail: describeItem(audit.type, item) || undefined,
          }))
        } else {
          rows = [{ dateLabel, name: `${audit.successful} items` }]
        }
        grouped[typeLabel] = [...(grouped[typeLabel] ?? []), ...rows]
      }
      const actionGroups: ReportActionGroup[] = Object.entries(grouped)
        .map(([typeLabel, items]) => ({ typeLabel, items }))
        .sort((a, b) => b.items.length - a.items.length)

      const data = {
        companyName: currentCompany?.name ?? "Company",
        from: range.from,
        to: range.to,
        slots,
        assigneeNames: Object.fromEntries(users.map(u => [u.id, u.name])),
        blastAdsBySlot,
        creativeCardsBySlot,
        budgetChangesBySlot,
        keyEvents,
        actionGroups,
      }
      const blob = await generateAgendaReportPdf(data)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = agendaReportFileName(data)
      anchor.click()
      URL.revokeObjectURL(url)
      toast.success("Report downloaded")
      onClose()
    } catch {
      toast.error("Failed to generate the report")
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={520}
      closeOnClickOutside
      classes={{
        container:
          "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl",
      }}
    >
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
            <DocumentArrowDownIcon className="h-5 w-5 text-system-brand-pink-400" />
            Action report
          </h3>
          <p className="mt-1 text-sm text-system-gray-400">
            A client-facing PDF of everything delivered in the period — ads
            released, creatives produced and approved, budget reviews.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-system-gray-300">From</span>
            <Input
              fullWidth
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-system-gray-300">To</span>
            <Input
              fullWidth
              type="date"
              value={toDate}
              min={fromDate}
              onChange={e => setToDate(e.target.value)}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat value={preview.adsReleased} label="Ads released" />
          <Stat value={preview.creativesProduced} label="Creatives" />
          <Stat value={preview.creativesApproved} label="Approved" />
          <Stat value={preview.budgetReviews} label="Budget reviews" />
        </div>
        <p className="text-xs text-system-gray-500">
          {isLoadingEnrichment
            ? "Loading activity, names and performance data…"
            : `${preview.done} of ${preview.total} planned activities completed · ${audits.filter(a => a.successful > 0).length} recorded actions in this period.`}
        </p>

        <div className="flex justify-end gap-2 border-t border-system-blue-600/30 pt-4">
          <Button
            variant={{ variant: "text" }}
            size="sm"
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            isLoading={isGenerating}
            disabled={isGenerating || isLoadingEnrichment}
            onClick={handleGenerate}
          >
            Download PDF
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
